"use client"
import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import {
  FileText,
  Shield,
  Users,
  CreditCard,
  AlertTriangle,
  Copyright,
  Eye,
  Gavel,
  RefreshCw,
  Mail,
  Phone,
  ChevronRight,
  Clock,
  CheckCircle,
} from "lucide-react"
import Header from "../components/Header"
import { Link } from "react-router-dom"
import Footer from "../components/Footer"

export const TermsOfServices = () => {
  const pageBnrImg = {
    position: "relative" ,
    backgroundImage: `url('./media/frontend/img/about/blog/1920x700/banner-2.jpg')`,
    backgroundPosition: "center",
    backgroundSize: "cover",
  }

  const [activeSection, setActiveSection] = useState("")
  const [readingProgress, setReadingProgress] = useState(0)

  const sections = [
    { id: "overview", title: "Overview", icon: FileText },
    { id: "eligibility", title: "Eligibility", icon: Users },
    { id: "accounts", title: "User Accounts", icon: Shield },
    { id: "role", title: "Role of Shefscloud.com", icon: Users },
    { id: "payments", title: "Payments and Fees", icon: CreditCard },
    { id: "cancellations", title: "Cancellations and Refunds", icon: RefreshCw },
    { id: "prohibited", title: "Prohibited Activities", icon: AlertTriangle },
    { id: "content", title: "Content and Intellectual Property", icon: Copyright },
    { id: "liability", title: "Limitation of Liability", icon: Shield },
    { id: "privacy", title: "Privacy", icon: Eye },
    { id: "governing", title: "Governing Law", icon: Gavel },
    { id: "changes", title: "Changes to These Terms", icon: RefreshCw },
    { id: "contact", title: "Contact Us", icon: Mail },
  ]

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      const progress = (scrollTop / docHeight) * 100
      setReadingProgress(progress)

      // Update active section based on scroll position
      const sectionElements = sections.map((section) => ({
        id: section.id,
        element: document.getElementById(section.id),
      }))

      const currentSection = sectionElements.find((section) => {
        if (section.element) {
          const rect = section.element.getBoundingClientRect()
          return rect.top <= 100 && rect.bottom >= 100
        }
        return false
      })

      if (currentSection) {
        setActiveSection(currentSection.id)
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] },
  }

  const fadeInLeft = {
    initial: { opacity: 0, x: -80 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] },
  }

  const scaleIn = {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.6, ease: "easeOut" },
  }

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  return (
    <>
      <Header />

      {/* Reading Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-primary/80 z-50 origin-left"
        style={{ scaleX: readingProgress / 100 }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: readingProgress / 100 }}
        transition={{ duration: 0.1 }}
      />

      {/* Enhanced Hero Section */}
      <section className="relative overflow-hidden">
        <div className="pageBanr py-32 relative" style={pageBnrImg}>
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/80"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/70"></div>

          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div
              className="absolute top-20 left-10 w-2 h-2 bg-primary rounded-full"
              animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
            />
            <motion.div
              className="absolute top-40 right-20 w-3 h-3 bg-white rounded-full"
              animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.8, 0.3] }}
              transition={{ duration: 3, repeat: Number.POSITIVE_INFINITY, delay: 1 }}
            />
            <motion.div
              className="absolute bottom-32 left-1/4 w-1 h-1 bg-primary rounded-full"
              animate={{ scale: [1, 2, 1], opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 2.5, repeat: Number.POSITIVE_INFINITY, delay: 0.5 }}
            />
          </div>

          <motion.div
            className="container mx-auto relative z-[1] text-center px-4"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <motion.div
              className="inline-block mb-6"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.8, delay: 0.5, type: "spring" }}
            >
              <div className="w-20 h-20 bg-gradient-to-br from-primary/20 to-primary/40 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/20">
                <FileText className="w-10 h-10 text-primary" />
              </div>
            </motion.div>

            <motion.h2
              className="font-bold text-5xl md:text-7xl text-white uppercase tracking-wider mb-6 bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Terms of Services
            </motion.h2>

            <motion.div
              className="w-32 h-1 bg-gradient-to-r from-transparent via-primary to-transparent mx-auto mb-8"
              initial={{ width: 0 }}
              animate={{ width: 128 }}
              transition={{ duration: 1, delay: 0.8 }}
            />

            <motion.ul
              className="inline-flex items-center space-x-2 text-white mb-0 bg-black/20 backdrop-blur-sm rounded-full px-6 py-3 border border-white/10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
            >
              <li className="text-lg">
                <Link
                  to="/"
                  className="!text-white hover:!text-primary transition-all duration-300 hover:scale-110 inline-block"
                >
                  Home
                </Link>
              </li>
              <li className="text-white/60">/</li>
              <li className="text-lg font-semibold text-primary">Terms of Services</li>
            </motion.ul>

            <motion.p
              className="text-xl text-white/90 mt-6 max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1.2 }}
            >
              Please read these terms carefully before using our services
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Enhanced Terms Content */}
      <section className="termsOfService py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-primary/3 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto lg:px-2 px-4 relative z-10">
          <div className="flex lg:flex-row flex-col gap-12">
            {/* Table of Contents - Sticky Sidebar */}
            <motion.div
              className="lg:w-1/4 w-full"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInLeft}
            >
              <div className="lg:sticky lg:top-24">
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary/10 to-primary/20 rounded-xl flex items-center justify-center">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">Contents</h3>
                  </div>

                  <nav className="space-y-2">
                    {sections.map((section, index) => {
                      const IconComponent = section.icon
                      return (
                        <motion.button
                          key={section.id}
                          onClick={() => scrollToSection(section.id)}
                          className={`w-full flex items-center space-x-3 p-3 rounded-xl text-left transition-all duration-300 group ${
                            activeSection === section.id
                              ? "bg-gradient-to-r from-primary/10 to-primary/5 text-primary border border-primary/20"
                              : "hover:bg-gray-50 text-gray-700 hover:text-primary"
                          }`}
                          whileHover={{ x: 5 }}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                        >
                          <IconComponent className="w-4 h-4 flex-shrink-0" />
                          <span className="text-sm font-medium flex-1">{section.title}</span>
                          <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                        </motion.button>
                      )
                    })}
                  </nav>
                </div>
              </div>
            </motion.div>

            {/* Main Content */}
            <motion.div
              className="lg:w-3/4 w-full"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {/* Introduction */}
              <motion.div className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-xl flex items-center justify-center">
                      <FileText className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold text-primary uppercase">Terms of Service</h1>
                      <div className="flex items-center space-x-2 mt-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <p className="text-base text-gray-700">Effective Date: 15 January 2025</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-primary/5 to-transparent rounded-2xl p-6 border-l-4 border-primary">
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Welcome to Shefs Cloud. By accessing or using our platform, you agree to these
                      Terms of Service. These Terms govern your access to and use of our Website, services,
                      and interactions between home shefs and customers. If you do not agree, please
                      refrain from using the Website.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Section 1: Overview */}
              <motion.div id="overview" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500/10 to-blue-600/20 rounded-xl flex items-center justify-center">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">1. Overview</h2>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50/50 to-transparent rounded-2xl p-6">
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Shefscloud.com provides a platform that connects home shefs with customers looking for
                      personalized meal preparation services. We act as an intermediary and are not responsible for the
                      actual preparation or delivery of food.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Section 2: Eligibility */}
              <motion.div id="eligibility" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500/10 to-green-600/20 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">2. Eligibility</h2>
                  </div>
                  <div className="bg-gradient-to-r from-green-50/50 to-transparent rounded-2xl p-6">
                    <ul className="space-y-4">
                      <li className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">
                          You have the legal capacity to enter into a binding agreement.
                        </span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">
                          You will provide accurate and complete registration information.
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </motion.div>

              {/* Section 3: User Accounts */}
              <motion.div id="accounts" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500/10 to-purple-600/20 rounded-xl flex items-center justify-center">
                      <Shield className="w-6 h-6 text-purple-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">3. User Accounts</h2>
                  </div>
                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-purple-50/50 to-transparent rounded-2xl p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <span className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 text-sm font-bold">
                          a
                        </span>
                        <span>Registration:</span>
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed ml-8">
                        To access certain features, you must create an account and provide accurate details.
                      </p>
                    </div>
                    <div className="bg-gradient-to-r from-purple-50/50 to-transparent rounded-2xl p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <span className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 text-sm font-bold">
                          b
                        </span>
                        <span>Account Security:</span>
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed ml-8">
                        You are responsible for maintaining the confidentiality of your account credentials. Notify us
                        immediately of any unauthorized use of your account.
                      </p>
                    </div>
                    <div className="bg-gradient-to-r from-purple-50/50 to-transparent rounded-2xl p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <span className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 text-sm font-bold">
                          c
                        </span>
                        <span>Termination of Accounts:</span>
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed ml-8">
                        We reserve the right to suspend or terminate your account for any violation of these Terms or
                        fraudulent activity.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Section 4: Role of Shefscloud.com */}
              <motion.div id="role" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500/10 to-orange-600/20 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-orange-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">4. Role of Shefscloud.com</h2>
                  </div>
                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-orange-50/50 to-transparent rounded-2xl p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <span className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 text-sm font-bold">
                          a
                        </span>
                        <span>Intermediary Only:</span>
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed ml-8">
                        shefscloud.com acts solely as a platform to connect Shefs and Customers. We do not prepare, deliver,
                        or supervise food services.
                      </p>
                    </div>
                    <div className="bg-gradient-to-r from-orange-50/50 to-transparent rounded-2xl p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <span className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 text-sm font-bold">
                          b
                        </span>
                        <span>Shefs Responsibilities:</span>
                      </h3>
                      <ul className="space-y-3 ml-8">
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                          <span className="text-lg text-gray-700">Comply with local food safety regulations.</span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                          <span className="text-lg text-gray-700">Deliver services as advertised on the platform.</span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                          <span className="text-lg text-gray-700">
                            Maintain a professional standard of hygiene and food quality.
                          </span>
                        </li>
                      </ul>
                    </div>
                    <div className="bg-gradient-to-r from-orange-50/50 to-transparent rounded-2xl p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <span className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 text-sm font-bold">
                          c
                        </span>
                        <span>User Responsibilities:</span>
                      </h3>
                      <ul className="space-y-3 ml-8">
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                          <span className="text-lg text-gray-700">
                            Provide accurate information for orders (e.g., delivery address, dietary preferences).
                          </span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                          <span className="text-lg text-gray-700">Communicate respectfully with Shefs.</span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                          <span className="text-lg text-gray-700">
                            Pay for services in accordance with the platform's policies.
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Section 5: Payments and Fees */}
              <motion.div id="payments" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/10 to-emerald-600/20 rounded-xl flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">5. Payments and Fees</h2>
                  </div>
                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-emerald-50/50 to-transparent rounded-2xl p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <span className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 text-sm font-bold">
                          a
                        </span>
                        <span>Payment Processing:</span>
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed ml-8">
                        Currently shefscloud only accepts cash-on-delivery, and online payment is not available.
                      </p>
                    </div>
                    <div className="bg-gradient-to-r from-emerald-50/50 to-transparent rounded-2xl p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <span className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 text-sm font-bold">
                          b
                        </span>
                        <span>Platform Fees:</span>
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed ml-8">
                        shefscloud.com may charge a service fee for using the platform from Shefs only. Any applicable
                        fees will be disclosed at checkout.
                      </p>
                    </div>
                    <div className="bg-gradient-to-r from-emerald-50/50 to-transparent rounded-2xl p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <span className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 text-sm font-bold">
                          c
                        </span>
                        <span>Refunds and Disputes:</span>
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed ml-8">
                        Refund policies vary depending on the Shef. In case of disputes, contact us at
                        info@shefscloud.com or WhatsApp at +92 320 2024035, and we will mediate to resolve the issue.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Section 6: Cancellations and Refunds */}
              <motion.div id="cancellations" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500/10 to-red-600/20 rounded-xl flex items-center justify-center">
                      <RefreshCw className="w-6 h-6 text-red-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">6. Cancellations and Refunds</h2>
                  </div>
                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-red-50/50 to-transparent rounded-2xl p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <span className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center text-red-600 text-sm font-bold">
                          a
                        </span>
                        <span>Shefs Cancellations:</span>
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed ml-8">
                        If a Shef cancels an order, Users are entitled to a full refund if any advance payment was made
                        by the customer.
                      </p>
                    </div>
                    <div className="bg-gradient-to-r from-red-50/50 to-transparent rounded-2xl p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <span className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center text-red-600 text-sm font-bold">
                          b
                        </span>
                        <span>User Cancellations:</span>
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed ml-8">
                        Order cancellation is allowed for some time after placing the order. After this time,
                        cancellation by the User will impact the User's profile registration if the Shef reports the
                        issue.
                      </p>
                    </div>
                    <div className="bg-gradient-to-r from-red-50/50 to-transparent rounded-2xl p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <span className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center text-red-600 text-sm font-bold">
                          c
                        </span>
                        <span>Force Majeure:</span>
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed ml-8">
                        shefscloud.com is not liable for cancellations or delays caused by events beyond our control,
                        such as natural disasters or emergencies.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Section 7: Prohibited Activities */}
              <motion.div id="prohibited" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-500/10 to-yellow-600/20 rounded-xl flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-yellow-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">7. Prohibited Activities</h2>
                  </div>
                  <div className="bg-gradient-to-r from-yellow-50/50 to-transparent rounded-2xl p-6">
                    <p className="text-lg text-gray-700 mb-4">You agree not to:</p>
                    <ul className="space-y-3">
                      <li className="flex items-start space-x-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">
                          Use the platform for unlawful or fraudulent purposes.
                        </span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">Post false or misleading information.</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">Harass, abuse, or harm other Users or Shefs.</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">
                          Violate any intellectual property or proprietary rights.
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </motion.div>

              {/* Section 8: Content and Intellectual Property */}
              <motion.div id="content" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500/10 to-indigo-600/20 rounded-xl flex items-center justify-center">
                      <Copyright className="w-6 h-6 text-indigo-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">8. Content and Intellectual Property</h2>
                  </div>
                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-indigo-50/50 to-transparent rounded-2xl p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <span className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 text-sm font-bold">
                          a
                        </span>
                        <span>User-Generated Content:</span>
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed ml-8">
                        You may upload reviews, comments, or other content to the platform. By doing so, you grant
                        shefscloud.com a non-exclusive, royalty-free license to use, modify, or distribute your content.
                      </p>
                    </div>
                    <div className="bg-gradient-to-r from-indigo-50/50 to-transparent rounded-2xl p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <span className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 text-sm font-bold">
                          b
                        </span>
                        <span>Ownership:</span>
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed ml-8">
                        All materials on the Website, including logos, text, and images, are owned by shefscloud.com or
                        its licensors. Unauthorized use is prohibited.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Section 9: Limitation of Liability */}
              <motion.div id="liability" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-pink-500/10 to-pink-600/20 rounded-xl flex items-center justify-center">
                      <Shield className="w-6 h-6 text-pink-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">9. Limitation of Liability</h2>
                  </div>
                  <div className="bg-gradient-to-r from-pink-50/50 to-transparent rounded-2xl p-6">
                    <p className="text-lg text-gray-700 mb-4">Shefscloud.com is not liable for:</p>
                    <ul className="space-y-3">
                      <li className="flex items-start space-x-3">
                        <Shield className="w-5 h-5 text-pink-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">Food quality, preparation, or safety issues.</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <Shield className="w-5 h-5 text-pink-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">
                          Losses arising from disputes between Users and Shefs.
                        </span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <Shield className="w-5 h-5 text-pink-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">
                          Indirect, incidental, or consequential damages resulting from the use of the platform.
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </motion.div>

              {/* Section 10: Privacy */}
              <motion.div id="privacy" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500/10 to-teal-600/20 rounded-xl flex items-center justify-center">
                      <Eye className="w-6 h-6 text-teal-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">10. Privacy</h2>
                  </div>
                  <div className="bg-gradient-to-r from-teal-50/50 to-transparent rounded-2xl p-6">
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Your use of the Website is governed by our Privacy Policy. Please review it to understand how we
                      collect, use, and protect your information.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Section 11: Governing Law */}
              <motion.div id="governing" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-gray-500/10 to-gray-600/20 rounded-xl flex items-center justify-center">
                      <Gavel className="w-6 h-6 text-gray-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">11. Governing Law</h2>
                  </div>
                  <div className="bg-gradient-to-r from-gray-50/50 to-transparent rounded-2xl p-6">
                    <p className="text-lg text-gray-700 leading-relaxed">
                      These Terms are governed by the laws of the country. Any disputes shall be resolved in the courts
                      of the country.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Section 12: Changes to These Terms */}
              <motion.div id="changes" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/10 to-cyan-600/20 rounded-xl flex items-center justify-center">
                      <RefreshCw className="w-6 h-6 text-cyan-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">12. Changes to These Terms</h2>
                  </div>
                  <div className="bg-gradient-to-r from-cyan-50/50 to-transparent rounded-2xl p-6">
                    <p className="text-lg text-gray-700 leading-relaxed">
                      We may update these Terms from time to time. Continued use of the Website after changes are posted
                      constitutes your acceptance of the revised Terms.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Section 13: Contact Us */}
              <motion.div id="contact" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-xl flex items-center justify-center">
                      <Mail className="w-6 h-6 text-primary" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">13. Contact Us</h2>
                  </div>
                  <div className="bg-gradient-to-r from-primary/5 to-transparent rounded-2xl p-6">
                    <p className="text-lg text-gray-700 mb-6">
                      For questions or concerns regarding these Terms, contact us at:
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                          <Mail className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-lg text-gray-700">
                            Email:{" "}
                            <a
                              href="mailto:info@shefscloud.com"
                              className="text-primary hover:text-primary/80 underline transition-colors duration-300"
                            >
                              info@shefscloud.com
                            </a>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                          <Phone className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-lg text-gray-700">
                            Phone:{" "}
                            <a
                              href="tel:+923202024035"
                              className="text-primary hover:text-primary/80 underline transition-colors duration-300"
                            >
                              +92 320 2024035
                            </a>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  )
}

export default TermsOfServices
